import React, { useState, useCallback, useEffect } from 'react';
import { Search, Database, DollarSign, BookOpen, BarChart3, Users, Zap, Rocket } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Card } from 'pokemon-tcg-sdk-typescript';
import { searchAllCards, getMostValuableCards, getAllSets, getSetCards } from '../services/pokemonTcgApi';
import SearchResults from '../components/SearchResults';
import debounce from 'lodash/debounce';
import { useTypingEffect } from '../hooks/useTypingEffect';
import SEOTags from '../components/SEOTags';
import LoadingScreen from '../components/LoadingScreen';
import { slugify } from '../utils/slugify';
import { useCurrencyStore } from '../lib/currencyStore';
import { formatPrice } from '../utils/formatPrice';
import { EnergyIcon } from '../components/icons/EnergyIcon';
import { generateHomeMetaDescription, generateHomeOpenGraph, generateHomeSchema } from '../utils/seoHelpers';
import { generateHomeCanonicalUrl } from '../utils/generateCanonicalUrl';
import CardImage from '../components/CardImage';

const SEARCH_HISTORY_KEY = 'pokemonTcgSearchHistory';

const FeatureIcons = {
  Database: () => (
    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" className="stroke-blue-500" strokeWidth="2">
        <animate
          attributeName="r"
          values="10;11;10"
          dur="2s"
          repeatCount="indefinite"
        />
      </circle>
      <path
        d="M6 12h12M12 6v12"
        className="stroke-blue-500"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <animate
          attributeName="stroke-dasharray"
          values="0,24;24,0;0,24"
          dur="2s"
          repeatCount="indefinite"
        />
      </path>
    </svg>
  ),
  DollarSign: () => (
    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
      <path
        d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"
        className="stroke-yellow-500"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <animate
          attributeName="stroke-dasharray"
          values="0,48;48,0;0,48"
          dur="3s"
          repeatCount="indefinite"
        />
      </path>
    </svg>
  ),
  BookOpen: () => (
    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
      <path
        d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"
        className="stroke-green-500"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <animate
          attributeName="d"
          values="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z;M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"
          dur="3s"
          repeatCount="indefinite"
        />
      </path>
      <path
        d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"
        className="stroke-green-500"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <animate
          attributeName="d"
          values="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z;M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"
          dur="3s"
          repeatCount="indefinite"
        />
      </path>
    </svg>
  ),
  BarChart: () => (
    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
      <path
        d="M18 20V10M12 20V4M6 20v-6"
        className="stroke-purple-500"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <animate
          attributeName="stroke-dasharray"
          values="0,24;24,0;0,24"
          dur="2s"
          repeatCount="indefinite"
        />
      </path>
    </svg>
  ),
  Zap: () => (
    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
      <path
        d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"
        className="stroke-red-500"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <animate
          attributeName="stroke-dasharray"
          values="0,48;48,0;0,48"
          dur="2s"
          repeatCount="indefinite"
        />
      </path>
    </svg>
  ),
  Rocket: () => (
    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
      <path
        d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"
        className="stroke-orange-500"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <animate
          attributeName="stroke-dasharray"
          values="0,24;24,0;0,24"
          dur="3s"
          repeatCount="indefinite"
        />
      </path>
      <path
        d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22 22 0 0 1-4 2z"
        className="stroke-orange-500"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <animate
          attributeName="stroke-dasharray"
          values="0,48;48,0;0,48"
          dur="3s"
          repeatCount="indefinite"
        />
      </path>
    </svg>
  )
};

export default function HomePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currency } = useCurrencyStore();
  const [searchQuery, setSearchQuery] = useState(() => {
    return location.state?.restoreSearch ? localStorage.getItem(SEARCH_HISTORY_KEY) || '' : '';
  });
  const [searchResults, setSearchResults] = useState<Card[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCards, setTotalCards] = useState<number>(0);
  const [totalSets, setTotalSets] = useState<number>(0);
  const [totalSeries, setTotalSeries] = useState<number>(0);
  const [statsLoading, setStatsLoading] = useState(true);
  const [valuableCards, setValuableCards] = useState<Card[]>([]);
  const [valuableCardsError, setValuableCardsError] = useState<string | null>(null);
  const [featuredLoading, setFeaturedLoading] = useState(true);
  const [latestSets, setLatestSets] = useState<Array<{ name: string, total: number, images?: { logo?: string } }>>([]);
  const [error, setError] = useState<string | null>(null);

  const placeholderText = useTypingEffect([
    "Search for 'Charizard'...",
    "Search for 'Pikachu'...",
    "Search for 'Mewtwo'...",
    "Search for 'Umbreon'...",
    "Search for 'Rayquaza'...",
  ], 100, 50, 2000);

  useEffect(() => {
    const fetchStats = async () => {
      setStatsLoading(true);
      try {
        const sets = await getAllSets();
        if (!sets) {
          setError('Unable to load card data. Please try again later.');
          return;
        }
        setTotalSets(sets.length);
        const uniqueSeries = new Set(sets.map(set => set.series));
        setTotalSeries(uniqueSeries.size);
        const cardCount = sets.reduce((acc, set) => acc + set.total, 0);
        setTotalCards(cardCount);
        const latest = sets
          .sort((a, b) => new Date(b.releaseDate).getTime() - new Date(a.releaseDate).getTime())
          .slice(0, 3);
        setLatestSets(latest);
      } catch (err) {
        console.error('Error fetching stats:', err);
        setError('Unable to load card data. Please try again later.');
      } finally {
        setStatsLoading(false);
      }
    };

    fetchStats();
  }, []);

  useEffect(() => {
    const fetchValuableCards = async () => {
      setFeaturedLoading(true);
      setValuableCardsError(null);
      try {
        const cards = await getMostValuableCards(10);
        if (cards && cards.length > 0) {
          setValuableCards(cards);
        } else {
          setValuableCardsError('No valuable cards found');
        }
      } catch (error) {
        console.error('Error fetching valuable cards:', error);
        setValuableCardsError('Failed to load valuable cards');
      } finally {
        setFeaturedLoading(false);
      }
    };

    fetchValuableCards();
  }, []);

  useEffect(() => {
    if (location.state?.restoreSearch && searchQuery) {
      performSearch(searchQuery);
    }
  }, [location.state]);

  const performSearch = useCallback(
    debounce(async (query: string) => {
      if (query.length < 2) {
        setSearchResults([]);
        return;
      }
      setLoading(true);
      try {
        const results = await searchAllCards(query);
        setSearchResults(results);
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setLoading(false);
      }
    }, 300),
    []
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    localStorage.setItem(SEARCH_HISTORY_KEY, query);
    performSearch(query);
  };

  const handleCardClick = (card: Card) => {
    const seriesSlug = slugify(card.set.series);
    const setSlug = slugify(card.set.name);
    const cardSlug = `${slugify(card.name)}-${card.number}`;
    navigate(`/${seriesSlug}/${setSlug}/${cardSlug}`, {
      state: { fromSearch: true }
    });
  };

  // Show error state if there's an error
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black p-4">
        <div className="bg-white/5 p-8 rounded-lg shadow-xl border border-white/10 max-w-md w-full text-center">
          <h2 className="text-2xl font-bold mb-4 text-white">Unable to Load</h2>
          <p className="text-white/60 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="w-full py-2 px-4 rounded-lg bg-white/10 hover:bg-white/20 text-white font-medium transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white p-4 sm:p-8">
      <SEOTags
        title={`PokeCardIndex - Search Over ${totalCards?.toLocaleString()} Pokémon Cards`}
        description={generateHomeMetaDescription(totalCards, totalSets)}
        keywords="pokemon cards, tcg, trading card game, card prices, card database, pokemon collection"
        openGraph={generateHomeOpenGraph(totalCards, totalSets)}
        canonicalUrl={generateHomeCanonicalUrl()}
        schema={generateHomeSchema(totalCards, totalSets)}
      />

      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12 sm:mb-24">
          <h1 className="text-3xl sm:text-5xl font-bold mb-4 sm:mb-6 bg-gradient-to-r from-white to-white/80 bg-clip-text text-transparent">
            The Pokémon Card Database
          </h1>
          <p className="text-base sm:text-xl text-white/60 mb-4 sm:mb-6" itemProp="description">
            Your comprehensive resource for Pokémon card information, prices, and collection tracking.
            Browse over {totalCards?.toLocaleString() || '18,000'} cards across {totalSets || '164'} sets.
          </p>
          <p className="text-base sm:text-lg text-green-500 font-medium animate-pulse mt-4 sm:mt-8">
            100% Free - No Premium Features or Paywalls!
          </p>
        </div>

        <div className="relative mb-8 sm:mb-16">
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder={placeholderText}
            className="w-full bg-white/5 text-white px-4 sm:px-6 py-4 sm:py-5 rounded-xl border border-white/10 focus:outline-none focus:ring-2 focus:ring-white/25 pl-12 sm:pl-14 placeholder-white/40 text-base sm:text-lg"
          />
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white/40" size={20} />
        </div>

        {searchQuery ? (
          <SearchResults cards={searchResults} loading={loading} />
        ) : (
          <>
            <div className="bg-gradient-to-br from-white/5 to-transparent border border-white/10 rounded-2xl p-4 sm:p-8 mb-12 sm:mb-24">
              <div className="flex flex-col lg:flex-row lg:items-center gap-8">
                <div className="flex-1">
                  <h2 className="text-xl sm:text-2xl font-bold mb-6">Database Overview</h2>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                      <h3 className="text-white/60 text-sm mb-1">Cards</h3>
                      <p className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-white to-white/80 bg-clip-text text-transparent">
                        {statsLoading ? (
                          <span className="animate-pulse">...</span>
                        ) : (
                          totalCards.toLocaleString()
                        )}
                      </p>
                    </div>
                    <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                      <h3 className="text-white/60 text-sm mb-1">Sets</h3>
                      <p className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-white to-white/80 bg-clip-text text-transparent">
                        {statsLoading ? <span className="animate-pulse">...</span> : totalSets}
                      </p>
                    </div>
                    <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                      <h3 className="text-white/60 text-sm mb-1">Series</h3>
                      <p className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-white to-white/80 bg-clip-text text-transparent">
                        {statsLoading ? <span className="animate-pulse">...</span> : totalSeries}
                      </p>
                    </div>
                    <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                      <h3 className="text-white/60 text-sm mb-1">Updates</h3>
                      <p className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-white to-white/80 bg-clip-text text-transparent">
                        Daily
                      </p>
                    </div>
                  </div>
                </div>

                <div className="lg:w-96">
                  <h3 className="text-lg font-bold mb-4">Latest Sets</h3>
                  <div className="space-y-3">
                    {statsLoading ? (
                      Array(3).fill(0).map((_, i) => (
                        <div key={i} className="animate-pulse">
                          <div className="h-4 bg-white/10 rounded w-3/4 mb-1"></div>
                          <div className="h-3 bg-white/10 rounded w-1/4"></div>
                        </div>
                      ))
                    ) : (
                      latestSets.map((set, index) => (
                        <Link
                          key={index}
                          to={`/scarlet-violet/${slugify(set.name)}`}
                          className="relative flex items-center gap-4 bg-white/5 rounded-lg p-4 hover:bg-white/10 transition-colors border border-white/10 overflow-hidden group"
                        >
                          {set.images?.logo && (
                            <div 
                              className="absolute inset-0 opacity-50 transition-opacity group-hover:opacity-75"
                              style={{
                                backgroundImage: `url(${set.images.logo})`,
                                backgroundSize: 'contain',
                                backgroundPosition: 'center',
                                backgroundRepeat: 'no-repeat',
                                transform: 'scale(1.0)'
                              }}
                            />
                          )}
                          <div className="relative z-10">
                            <h4 className="font-medium text-sm">{set.name}</h4>
                            <p className="text-white/60 text-xs">{set.total} cards</p>
                          </div>
                        </Link>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8 mb-12 sm:mb-24">
              <div className="group bg-gradient-to-br from-blue-950/50 to-transparent border border-white/10 rounded-2xl p-6 sm:p-8 hover:border-white/20 transition-all duration-300">
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 bg-blue-500/10 rounded-xl">
                    <FeatureIcons.Database />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold">Extensive Database</h3>
                </div>
                <p className="text-white/60 text-sm sm:text-base">
                  Access over {totalCards?.toLocaleString()} cards across all sets with high-quality images and detailed information.
                </p>
              </div>
              
              <div className="group bg-gradient-to-br from-yellow-950/50 to-transparent border border-white/10 rounded-2xl p-6 sm:p-8 hover:border-white/20 transition-all duration-300">
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 bg-yellow-500/10 rounded-xl">
                    <FeatureIcons.DollarSign />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold">Real-time Prices</h3>
                </div>
                <p className="text-white/60 text-sm sm:text-base">
                  Track market values updated daily to make informed collecting decisions.
                </p>
              </div>
              
              <div className="group bg-gradient-to-br from-green-950/50 to-transparent border border-white/10 rounded-2xl p-6 sm:p-8 hover:border-white/20 transition-all duration-300">
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 bg-green-500/10 rounded-xl">
                    <FeatureIcons.BookOpen />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold">Collection Tracking</h3>
                </div>
                <p className="text-white/60 text-sm sm:text-base">
                  Manage and organize your cards with our powerful collection tools.
                </p>
              </div>
              
              <div className="group bg-gradient-to-br from-purple-950/50 to-transparent border border-white/10 rounded-2xl p-6 sm:p-8 hover:border-white/20 transition-all duration-300">
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 bg-purple-500/10 rounded-xl">
                    <FeatureIcons.BarChart />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold">Advanced Analytics</h3>
                </div>
                <p className="text-white/60 text-sm sm:text-base">
                  Track collection value and completion with detailed analytics.
                </p>
              </div>
              
              <div className="group bg-gradient-to-br from-red-950/50 to-transparent border border-white/10 rounded-2xl p-6 sm:p-8 hover:border-white/20 transition-all duration-300">
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 bg-red-500/10 rounded-xl">
                    <FeatureIcons.Zap />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold">100% Free</h3>
                </div>
                <p className="text-white/60 text-sm sm:text-base">
                  No Premium Features or Paywalls! Everything is completely free.
                </p>
              </div>
              
              <div className="group bg-gradient-to-br from-orange-950/50 to-transparent border border-white/10 rounded-2xl p-6 sm:p-8 hover:border-white/20 transition-all duration-300">
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 bg-orange-500/10 rounded-xl">
                    <FeatureIcons.Rocket />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold">Fast Search</h3>
                </div>
                <p className="text-white/60 text-sm sm:text-base">
                  Find any card instantly with our powerful search engine.
                </p>
              </div>
            </div>

            <div className="mt-12 sm:mt-24 text-center">
              <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6">Start Your Collection Today</h2>
              <p className="text-base sm:text-lg text-white/60 mb-8 sm:mb-12 max-w-2xl mx-auto">
                Join thousands of collectors using PokeCardIndex to track their collections.
                100% Free - Join our Discord community to connect with other collectors.
              </p>
              <Link
                to="/auth?register=true"
                className="inline-flex items-center gap-3 px-8 sm:px-10 py-3 sm:py-4 bg-white/10 hover:bg-white/20 rounded-xl text-white font-medium transition-all duration-300 hover:scale-105 text-base sm:text-lg"
              >
                Create Free Account
                <Zap className="w-5 h-5" />
              </Link>
            </div>

            {!valuableCardsError && !featuredLoading && valuableCards.length > 0 && (
              <div className="mt-12 sm:mt-24">
                <div className="bg-white/5 border border-white/10 p-4 sm:p-6 rounded-xl">
                  <h2 className="text-xl font-semibold mb-4">Most Valuable Cards</h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                    {valuableCards.map((card, index) => (
                      <article
                        key={card.id}
                        className="group relative bg-black/50 rounded-lg overflow-hidden cursor-pointer hover:ring-2 hover:ring-white/25 transition-all shadow-lg"
                        onClick={() => handleCardClick(card)}
                      >
                        <div className="relative aspect-[2.5/3.5]">
                          <CardImage
                            src={card.images.small}
                            alt={card.name}
                            cardId={card.id}
                            className="absolute inset-0 w-full h-full object-cover"
                            width={245}
                            quality={75}
                            loading={index < 5 ? "eager" : "lazy"}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-lg">
                            <div className="absolute bottom-0 inset-x-0 p-3">
                              <div className="flex items-center justify-between mb-1">
                                <div>
                                  <p className="text-sm font-medium text-white truncate">{card.name}</p>
                                  <p className="text-xs text-white/60">
                                    {card.number}/{card.set.printedTotal}
                                  </p>
                                </div>
                                {card.types?.[0] && (
                                  <EnergyIcon type={card.types[0]} />
                                )}
                              </div>
                              <p className="text-sm font-medium text-white/80">
                                {formatPrice(card.market_price_trend_usd || card.cardmarket?.prices?.trendPrice, currency)}
                              </p>
                            </div>
                          </div>
                        </div>
                      </article>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}